import { useCallback } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useStudentEnrollments from '@/data/hook/enrollment/useStudentEnrollments';
import useActiveCourseProgress from '@/data/hook/student-dashboard/useActiveCourseProgress';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useStudentContext from '@/data/hook/useStudentContext';
import { studentDashboardQueries } from '@/data/query-keys/studentDashboardQueryKey';
import { StudentDashboardFilters } from '@/data/services/studentDashboardStatsServices';
import StudentDashboardStats from '@/models/StudentDashboard';

export type CourseProgressSelection = {
  slug?: string;
  fallbackToFirst: boolean;
  select(courseProgress: StudentDashboardStats): void;
};

type UseStudentDashboardCourseProgressesProps = {
  studentId?: number;
  selection: CourseProgressSelection;
};

export default function useStudentDashboardCourseProgresses({
  studentId,
  selection: { slug, fallbackToFirst, select },
}: UseStudentDashboardCourseProgressesProps) {
  const { selectProgress: legacySelectProgress } = useStudentContext();

  const { enrollments, isLoading: isLoadingEnrollments } =
    useStudentEnrollments({ studentId });

  const filters: StudentDashboardFilters = {
    studentId,
    isActive: true,
    startSoon: enrollments.length > 1 ? false : undefined,
    ordering: '-created_at',
  };

  const {
    results: courseProgresses,
    isInitialLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...studentDashboardQueries.courseProgressesInfinite(filters),
    enabled: !!studentId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { activeCourseProgress, isLoading: isResolvingActive } =
    useActiveCourseProgress({
      courseProgresses,
      hasNextPage: !!hasNextPage,
      filters,
      slug,
      fallbackToFirst,
    });

  const selectCourseProgress = useCallback(
    (selectedId: number) => {
      const selected = courseProgresses.find(({ id }) => id === selectedId);
      if (selected) select(selected);
      legacySelectProgress(selectedId);
    },
    [courseProgresses, select, legacySelectProgress],
  );

  return {
    courseProgresses,
    activeCourseProgress,
    selectCourseProgress,
    isLoading: isInitialLoading || isLoadingEnrollments || isResolvingActive,
    error,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
